// React Dependencies
import React, { useRef, useState, useReducer, useEffect } from 'react';

// External Dependencies
import moment from 'moment';
import Lightbox from 'react-image-lightbox';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, Dialog, FormControlLabel, IconButton, Radio, RadioGroup, TextField, Tooltip, Typography, Checkbox } from '@material-ui/core';
import { Visibility as VisibilityIcon, FilterHdr as ImageIcon, CloudUpload as CloudUploadIcon } from '@material-ui/icons';
import { toast } from 'react-toastify';

// Internal Dependencies
import OverLoading from '../../../global/OverLoading';
import apiService from '../../../../services.js/apiService';
import FixedCropper from '../../../global/image/FixedCropper';
import { IfFalseThrowError, IsEmpty, IsValidWwwAndHttpUrl, IsValidHttpsUrl, IsTrue, IfEmptyThrowError } from '../../../../common/checks';
import { Trim } from '../../../../common/util';
import { BinaryOneMBSize, IMAGE_FORMATS, Status, Error } from '../../../../common/constants';
import Colours from '../../../../styles/colours';
import ColorPickerInput from './ColorPickerInput';
import { updateModalContent as updateModalAction, toggleModal as toggleModalAction } from '../../../../actions/modal';

const defaultImageObject = { file: null, data: null };
const defaultCropperState = { image: defaultImageObject, isCropping: false };
const defaultInputState = {
  name:            '',
  startDate:       null,
  endDate:         null,
  question1:       '',
  question2:       '',
  backgroundColor: Colours.LightGray1,
  btnColor:        Colours.Red1,
  submitInfoText:  '',
  linkText:        '',
  link:            '',
  status:          Status.Draft,
};

const Info = ({ match, updateModalContent, toggleModal }) => {
  const { pageId, luckyDrawId } = match.params;

  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [initialStatus, setInitialStatus] = useState(null);
  const [isOpenViewer, setIsOpenViewer] = useState(false);
  const [isBgColourPickerOpen, setIsBgColourPickerOpen] = useState(false);
  const [isBtnColourPickerOpen, setIsBtnColourPickerOpen] = useState(false);
  const [isDisplayLinkButton, setIsDisplayLinkButton] = useState(false);

  const [cropperModal, setCropperModal] = useReducer((state, action) => ({ ...state, ...action }), defaultCropperState);
  const [inputs, setInputs] = useReducer((state, action) => ({ ...state, ...action }), defaultInputState);

  const isDeactivated = initialStatus === Status.Deactivated;
  const isDraft = initialStatus === Status.Draft;
  const isLive = initialStatus === Status.Live;

  const checkStatusTransitionDisabled = (status) => {
    if (isLive) {
      return status === Status.Draft;
    } else if (isDraft) {
      return status === Status.Deactivated;
    } else if (isDeactivated) {
      return [Status.Live, Status.Draft].includes(status);
    }
    return false;
  };

  const handleChange = (e) => {
    setInputs({ [e.target.name]: e.target.value });
  };

  const handleIsDisplayLinkButton = () => {
    if (IsTrue(isDisplayLinkButton)) {
      setInputs({ linkText: '', link: '' });
    }

    setIsDisplayLinkButton(!isDisplayLinkButton);
  };

  const handleSubmit = async (toastMessage = '') => {
    try {
      setIsLoading(true);

      if (!IsEmpty(inputs.startDate) && !IsEmpty(inputs.endDate)) {
        IfFalseThrowError(moment(inputs.endDate) > moment(inputs.startDate), 'End date should be later than the starting date');
      }

      const trimmedLinkText = Trim(inputs.linkText, false, false);
      const trimmedLinkUrl = Trim(inputs.link, false, false);
      if (IsTrue(isDisplayLinkButton)) {
        IfEmptyThrowError(trimmedLinkText, Error.LinkTextRequired);
        IfEmptyThrowError(trimmedLinkUrl, Error.LinkUrlRequired);

        IfFalseThrowError(
          !!(IsValidWwwAndHttpUrl(trimmedLinkUrl) || IsValidHttpsUrl(trimmedLinkUrl)), `Link "${trimmedLinkUrl}" should be a valid URL`,
        );
      }

      const formattedInputs = {
        ...inputs,
        linkText:  trimmedLinkText,
        link:      trimmedLinkUrl,
        startDate: IsEmpty(inputs.startDate) ? null : moment(inputs.startDate).utcOffset(8, true).startOf('day').format(),
        endDate:   IsEmpty(inputs.endDate) ? null : moment(inputs.endDate).utcOffset(8, true).endOf('day').format(),
      };

      await apiService.updateLuckyDraw({ pageId, luckyDrawId }, formattedInputs);
      setInitialStatus(inputs.status);
      toast.success(toastMessage);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateModal = (status) => {
    updateModalContent({
      title:        status.title,
      content:      status.content,
      actionButton: {
        actionText: status.actionText,
        actionType: 'danger',
        action:     async () => {
          try {
            setIsLoading(true);
            handleSubmit(status.toast);
          } catch (err) {
            toast.error(err.message);
          } finally {
            setIsLoading(false);
          }
        },
      },
      cancelText:       'Cancel',
      closeAfterAction: true,
    });
    toggleModal();
  };

  const closeCropDialog = () => {
    setCropperModal({ isCropping: false, image: defaultImageObject });
  };

  const handleSaveImage = async (file) => {
    try {
      setIsLoading(true);
      if (IsEmpty(file)) {
        setCropperModal({ ...defaultCropperState });
        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        const formData = new FormData();
        formData.append('image', file);
        try {
          const imageUrl = await apiService.uploadLuckyDrawMedia({ pageId, luckyDrawId }, formData, file);
          setCropperModal({ ...defaultCropperState, image: { data: imageUrl } });
          setImage(imageUrl);
          toast.success('Lucky draw image has been updated');
        } catch (err) {
          toast.error(err.message);
        } finally {
          setIsLoading(false);
        }
      };
      return true;
    } catch (err) {
      toast.error(err.message);
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = () => {
    const statusContent = {
      Live: {
        title:      'Live Lucky Draw?',
        content:    'Once Lucky Draw is Live, it cannot be updated back to Draft',
        actionText: 'Live Lucky Draw',
        toast:      'Lucky draw is now Live.',
      },
      Deactivated: {
        title:      'Deactivate Lucky Draw?',
        content:    'Once Lucky Draw is Deactivated, it cannot be updated back to Live',
        actionText: 'Deactivate Lucky Draw',
        toast:      'Lucky draw is Deactivated.',
      },
    };

    if (inputs.status === Status.Live && initialStatus === Status.Draft) {
      handleUpdateModal(statusContent.Live);
    } else if (inputs.status === Status.Deactivated) {
      handleUpdateModal(statusContent.Deactivated);
    } else {
      handleSubmit('Successfully updated lucky draw.');
    }
  };

  const handleImageChange = async ({ target: fileInput }) => {
    if (fileInput.files.length === 0) {
      return;
    }

    const file = fileInput.files[0];

    if (file.size > BinaryOneMBSize * 2) {
      toast.error('Maximum file size of 2MB exceeded.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const imageObj = { file, data: reader.result };
      const cropperTriggered = { isCropping: true, image: imageObj };
      setCropperModal({ ...cropperTriggered });

      // clear file input
      fileInputRef.current.value = '';
    };
    reader.readAsDataURL(file);
  };

  const fetchLuckyDraw = async () => {
    try {
      setIsLoading(true);
      let {
        name, startDate, endDate, question1, question2, status, config, image: fetchedImage,
      } = await apiService.getLuckyDraw(pageId, luckyDrawId);

      let { backgroundColor, btnColor, submitInfoText, linkText, link } = !IsEmpty(config) ? config : {};

      startDate = !IsEmpty(startDate) ? moment(startDate).utcOffset('+08').local(true).format('YYYY-MM-DD') : null;
      endDate = !IsEmpty(endDate) ? moment(endDate).utcOffset('+08').local(true).format('YYYY-MM-DD') : null;
      backgroundColor = !IsEmpty(backgroundColor) ? backgroundColor : inputs.backgroundColor;
      btnColor = !IsEmpty(btnColor) ? btnColor : inputs.btnColor;

      setInitialStatus(status);
      setInputs({ name, startDate, endDate, question1, question2, status, backgroundColor, btnColor, submitInfoText, linkText, link });
      setImage(fetchedImage);
      setIsDisplayLinkButton(!IsEmpty(linkText) && !IsEmpty(link));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLuckyDraw();
  }, []);

  return (
    <>
      {isLoading && <OverLoading />}

      <div className="container-fluid pl-5 ml-3">
        <div className="row mb-4">
          <div className="col-12 col-xl-6 pl-0">
            <input
              hidden
              accept={IMAGE_FORMATS}
              type="file"
              onChange={handleImageChange}
              ref={fileInputRef}
            />
            {IsEmpty(image) && (
              <Button
                onClick={() => fileInputRef.current.click()}
                disabled={isDeactivated}
                className="mx-2 mx-sm-0"
                style={{ width: '95%', height: 180, minHeight: 50, backgroundColor: Colours.LightGray1, borderRadius: 15 }}
              >
                <div className="d-flex flex-column justify-content-center align-items-center">
                  <ImageIcon fontSize="large" />
                  <Typography style={{ fontSize: 11, textTransform: 'none' }}>+ Click to upload</Typography>
                </div>
              </Button>
            )}
            <div style={{ display: (image ? 'flex' : 'none'), width: '250px' }} className="mt-2 flex-column">
              <img
                alt="Lucky Draw"
                src={image}
                style={{
                  width:        '100%',
                  borderRadius: '10px',
                  boxShadow:    '0 1px 2px 1px rgba(0, 0, 0, .1)',
                }}
              />
              <div className="d-flex mt-2">
                {!isDeactivated && (
                  <div className="mr-2">
                    <Tooltip title="Upload">
                      <IconButton onClick={() => fileInputRef.current.click()} className="shadow-sm bg-white">
                        <CloudUploadIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                )}
                <div className="mr-2">
                  <Tooltip title="View">
                    <IconButton onClick={() => setIsOpenViewer(true)} className="shadow-sm bg-white">
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-12 col-xl-6 pl-0">
            <Typography style={{ fontSize: 13 }} className="mb-1">
              Lucky Draw Name
            </Typography>
            <div>
              <TextField
                disabled={isDeactivated}
                multiline
                name="name"
                value={inputs.name}
                variant="outlined"
                onChange={handleChange}
                inputProps={{ style: { fontSize: 13 } }}
                size="small"
                className="w-100"
              />
            </div>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-12 col-xl-6 pl-0 d-flex align-items-center" style={{ gap: '1rem' }}>
            <div style={{ width: '30%' }}>
              <Typography style={{ fontSize: 13 }} className="mb-1">
                Start Date
              </Typography>
              <TextField
                disabled={isDeactivated}
                name="startDate"
                fullWidth
                value={inputs.startDate}
                type="date"
                variant="outlined"
                size="small"
                onChange={handleChange}
                helperText="at 00:00:00"
                FormHelperTextProps={{ style: { marginLeft: 0 } }}
                inputProps={{ style: { fontSize: 13 } }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </div>

            <Typography style={{ fontSize: 13 }} className="mt-1">-</Typography>

            <div style={{ width: '30%' }}>
              <Typography style={{ fontSize: 13 }} className="mb-1">
                End Date
              </Typography>
              <TextField
                disabled={isDeactivated}
                name="endDate"
                fullWidth
                value={inputs.endDate}
                type="date"
                variant="outlined"
                size="small"
                onChange={handleChange}
                inputProps={{ style: { fontSize: 13 } }}
                helperText="at 23:59:59"
                FormHelperTextProps={{ style: { marginLeft: 0 } }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </div>
          </div>
        </div>

        <div className="row mt-5 mb-3">
          <div className="col-12 col-xl-6 pl-0">
            <Typography variant="h5" style={{ fontWeight: 'bold' }}>Interface Settings</Typography>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-12 col-xl-6 pl-0">
            <ColorPickerInput
              label="Select background colour"
              colour={inputs.backgroundColor}
              onChange={(value) => setInputs({ backgroundColor: value })}
              onClose={() => setIsBgColourPickerOpen(false)}
              isOpen={isBgColourPickerOpen}
              isDisabled={isDeactivated}
              onButtonClick={() => {
                setIsBgColourPickerOpen((prevState) => !prevState);
              }}
            />
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-12 col-xl-6 pl-0">
            <ColorPickerInput
              label="Select button colour"
              colour={inputs.btnColor}
              onChange={(value) => setInputs({ btnColor: value })}
              onClose={() => setIsBtnColourPickerOpen(false)}
              isOpen={isBtnColourPickerOpen}
              isDisabled={isDeactivated}
              onButtonClick={() => {
                setIsBtnColourPickerOpen((prevState) => !prevState);
              }}
            />
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-12 col-xl-6 pl-0">
            <Typography style={{ fontSize: 13 }} className="mb-1">
              Question 1
            </Typography>
            <div>
              <TextField
                disabled={isDeactivated}
                multiline
                rows={2}
                name="question1"
                value={inputs.question1}
                variant="outlined"
                onChange={handleChange}
                inputProps={{ style: { fontSize: 13 } }}
                size="small"
                className="w-100"
              />
            </div>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-12 col-xl-6 pl-0">
            <Typography style={{ fontSize: 13 }} className="mb-1">
              Question 2
            </Typography>
            <div>
              <TextField
                disabled={isDeactivated}
                multiline
                rows={2}
                name="question2"
                value={inputs.question2}
                variant="outlined"
                onChange={handleChange}
                inputProps={{ style: { fontSize: 13 } }}
                size="small"
                className="w-100"
              />
            </div>
          </div>
        </div>

        <div className="row mb-3 pb-1">
          <div className="col-12 col-xl-6 pl-0">
            <Typography style={{ fontSize: 13 }} className="mb-1">
              Submit Info Text
            </Typography>
            <div>
              <TextField
                disabled={isDeactivated}
                multiline
                rows={2}
                name="submitInfoText"
                value={inputs.submitInfoText}
                variant="outlined"
                onChange={handleChange}
                inputProps={{ style: { fontSize: 13 } }}
                size="small"
                className="w-100"
              />
            </div>
          </div>
        </div>

        <div className="row mb-1">
          <div className="col-12 col-xl-6 pl-0">
            <FormControlLabel
              control={(
                <Checkbox
                  onChange={handleIsDisplayLinkButton}
                  checked={isDisplayLinkButton}
                  disabled={isDeactivated}
                />
              )}
              label="Display a link button"
            />
          </div>
        </div>

        {isDisplayLinkButton && (
          <>
            <div className="row mb-4">
              <div className="col-12 col-xl-6 pl-0">
                <Typography style={{ fontSize: 13 }} className="mb-1">
                  Link Text
                </Typography>
                <div>
                  <TextField
                    disabled={isDeactivated}
                    name="linkText"
                    value={inputs.linkText}
                    variant="outlined"
                    onChange={handleChange}
                    inputProps={{ style: { fontSize: 13 } }}
                    size="small"
                    className="w-100"
                  />
                </div>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-12 col-xl-6 pl-0">
                <Typography style={{ fontSize: 13 }} className="mb-1">
                  Link URL
                </Typography>
                <div>
                  <TextField
                    disabled={isDeactivated}
                    name="link"
                    value={inputs.link}
                    variant="outlined"
                    onChange={handleChange}
                    inputProps={{ style: { fontSize: 13 } }}
                    size="small"
                    className="w-100"
                  />
                </div>
              </div>
            </div>
          </>
        )}

        <div className="row mb-4">
          <div className="col-12 col-xl-6 pl-0">
            <Typography style={{ fontSize: 13 }}>
              Status
            </Typography>
            <RadioGroup row name="status" value={inputs.status} onChange={handleChange} defaultValue={Status.Draft}>
              {[Status.Draft, Status.Live, Status.Deactivated].map((status) => {
                const isDisabled = checkStatusTransitionDisabled(status);

                return (
                  <div className="mr-3">
                    <FormControlLabel
                      value={status}
                      control={<Radio color="secondary" disabled={isDisabled} />}
                      style={{ fontSize: 13 }}
                      label={status}
                    />
                  </div>
                );
              })}
            </RadioGroup>
          </div>
        </div>

        <div className="row mb-2">
          <div className="col-12 col-xl-6 pl-0 d-flex justify-content-center">
            <Button
              disabled={isDeactivated}
              variant="contained"
              color="secondary"
              onClick={handleStatusUpdate}
              style={{ width: '80%' }}
            >
              Save
            </Button>
          </div>
        </div>
      </div>

      {/* Cropper Modal */}
      <Dialog fullScreen open={cropperModal.isCropping} onClose={closeCropDialog}>
        <FixedCropper
          width={4}
          height={3}
          cropPixelWidth={258}
          cropPixelHeight={400}
          outputPixelWidth={800}
          outputPixelHeight={1238}
          type="upload"
          label="Lucky Draw Image"
          src={cropperModal.image.data}
          file={cropperModal.image.file}
          handleClose={closeCropDialog}
          handleSave={handleSaveImage}
          uploading={isLoading}
        />
      </Dialog>

      {/* Image viewer modal */}
      {isOpenViewer && (
        <Lightbox
          animationDisabled
          mainSrc={image}
          onCloseRequest={() => setIsOpenViewer(false)}
        />
      )}
    </>
  );
};

Info.propTypes = {
  match:              PropTypes.shape(Object).isRequired,
  updateModalContent: PropTypes.func.isRequired,
  toggleModal:        PropTypes.func.isRequired,
};

export default connect(null, { toggleModal: toggleModalAction, updateModalContent: updateModalAction })(Info);
